import { entryToIndication, RolodexIndication } from 'features/indicationTriage'
import { FfsRegionClassification } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsRegionClassificationSchema'
import { parseRegionClassification } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsRegionRouting'
import {
  buildInspectionsByComponentId,
  sortComponentsForTabs,
} from 'features/libra/ticket/selectComponentInspection'
import {
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoComponentDataset,
  GeckoEntry,
  RolodexEntryType,
} from 'types'

import { FfsReportRecord, parseReportSummary } from './ffsReportRecord'

/**
 * Turns raw Rolodex entries into the shape the close-out rollup consumes.
 *
 * Pure and separate from `useFfsUnitRollup` because this is where the awkward
 * cases live — an indication entry that throws when parsed, a report whose
 * owning LTA cannot be identified, a component with no resolvable inspection —
 * and those are exactly the paths that must be testable without mounting React
 * and mocking four queries. The hook is left holding only the queries and the
 * selection state.
 */

/** One LTA as the queries produced it, before any FFS judgement is applied. */
export interface LtaInput {
  ltaId: string
  title: string
  classification: FfsRegionClassification | null
  /**
   * The indication entry itself would not parse. `entryToIndication` throws on
   * pre-migration `data.stats`, so parsing is defensive and the failure is
   * recorded here rather than letting one legacy row blank the whole view.
   */
  parseFailed: boolean
}

/** Everything the four queries produced, already parsed. No React, no Rolodex. */
export interface FfsUnitData {
  components: ReadonlyArray<{
    componentId: string
    name: string
    inspectionId: string
  }>
  /** Components the unit has but close-out cannot assess. */
  excludedComponents: ReadonlyArray<{
    componentId: string
    name: string
    reason: 'no-inspection'
  }>
  /** Present iff the component's deterministic LTA-set entry was found. */
  ltaSetByComponent: ReadonlyMap<string, { id: string; updatedAt: string }>
  ltasByComponent: ReadonlyMap<string, ReadonlyArray<LtaInput>>
  reportsByLta: ReadonlyMap<string, ReadonlyArray<FfsReportRecord>>
  reportParseFailuresByLta: ReadonlyMap<string, number>
  /** Report rows whose `indication` tag was unreadable — cannot be attributed. */
  unattributableReportFailures: number
}

/**
 * Split the unit's components into those close-out can assess and those it
 * cannot.
 *
 * `buildInspectionsByComponentId` OMITS components with no resolvable
 * inspection; that omission is precisely the excluded list, so it is captured
 * here rather than silently dropped.
 */
export function deriveComponents(source: {
  components: ReadonlyArray<GeckoComponent>
  entries: ReadonlyArray<GeckoEntry>
  componentDataFrames: ReadonlyArray<GeckoComponentDataFrame>
  componentDatasets: ReadonlyArray<GeckoComponentDataset>
}): Pick<FfsUnitData, 'components' | 'excludedComponents'> {
  const sorted = sortComponentsForTabs(source.components)
  const inspectionEntries = source.entries.filter(
    (e) => e.type === RolodexEntryType.INSPECTION
  )
  const inspectionByComponent = buildInspectionsByComponentId(
    sorted,
    inspectionEntries,
    source.componentDataFrames,
    source.componentDatasets
  )
  const named = (c: GeckoComponent) => c.data.name || c.id

  return {
    components: sorted
      .filter((c) => c.id in inspectionByComponent)
      .map((c) => ({
        componentId: c.id,
        name: named(c),
        inspectionId: inspectionByComponent[c.id],
      })),
    excludedComponents: sorted
      .filter((c) => !(c.id in inspectionByComponent))
      .map((c) => ({
        componentId: c.id,
        name: named(c),
        reason: 'no-inspection' as const,
      })),
  }
}

/**
 * Which components actually have their expected LTA set. Absence is meaningful:
 * it is what distinguishes "binning never ran" from "binning found nothing".
 */
export function matchLtaSets(
  expectedSetIdByComponent: ReadonlyMap<string, string>,
  setEntries: ReadonlyArray<GeckoEntry>
): FfsUnitData['ltaSetByComponent'] {
  const byId = new Map(setEntries.map((e) => [e.id, e]))
  const found = new Map<string, { id: string; updatedAt: string }>()
  for (const [componentId, setId] of expectedSetIdByComponent) {
    const entry = byId.get(setId)
    if (entry)
      found.set(componentId, { id: setId, updatedAt: entry.updated_at })
  }
  return found
}

/**
 * Parse LTA indication entries and group them by component.
 *
 * `entryToIndication` THROWS on pre-migration `data.stats` — it feeds undefined
 * numbers to `GeckoMeasurement`'s constructor — and close-out reads every LTA on
 * a unit at once, so an unguarded call would let a single legacy row blank the
 * entire sign-off view. Both the id and the owning set are readable straight off
 * the raw entry, so a failure still lands on the right row and blocks only that
 * one LTA.
 */
export function parseLtas(
  entries: ReadonlyArray<GeckoEntry>,
  ltaSetByComponent: FfsUnitData['ltaSetByComponent']
): { ltasByComponent: FfsUnitData['ltasByComponent']; ltaIds: string[] } {
  const componentBySetId = new Map(
    [...ltaSetByComponent].map(([componentId, set]) => [set.id, componentId])
  )
  const byComponent = new Map<string, LtaInput[]>()
  const ltaIds: string[] = []

  for (const entry of entries) {
    const setId = entry.tags?.[RolodexEntryType.INDICATION_SET]
    const componentId =
      typeof setId === 'string' ? componentBySetId.get(setId) : undefined
    if (!componentId) continue

    let indication: RolodexIndication | null = null
    try {
      indication = entryToIndication(entry.raw)
    } catch {
      indication = null
    }

    ltaIds.push(entry.id)
    const list = byComponent.get(componentId) ?? []
    list.push({
      ltaId: entry.id,
      title: indication?.title || entry.id,
      classification: indication
        ? parseRegionClassification(
            indication.customInfo?.ffsRegionClassification
          )
        : null,
      parseFailed: indication === null,
    })
    byComponent.set(componentId, list)
  }
  return { ltasByComponent: byComponent, ltaIds }
}

/**
 * Index saved reports by the LTA they belong to.
 *
 * A report that will not parse is ATTRIBUTED to its LTA rather than dropped, so
 * it blocks only that LTA; dropping it could leave a stale sibling looking like
 * the governing report. One with no readable `indication` tag cannot be
 * attributed at all and is counted for the unit-level blocker.
 */
export function indexReports(
  entries: ReadonlyArray<GeckoEntry>
): Pick<
  FfsUnitData,
  'reportsByLta' | 'reportParseFailuresByLta' | 'unattributableReportFailures'
> {
  const reportsByLta = new Map<string, FfsReportRecord[]>()
  const reportParseFailuresByLta = new Map<string, number>()
  let unattributableReportFailures = 0

  for (const entry of entries) {
    const outcome = parseReportSummary(entry)
    if (outcome.ok) {
      const list = reportsByLta.get(outcome.summary.indicationId) ?? []
      list.push(outcome.summary)
      reportsByLta.set(outcome.summary.indicationId, list)
    } else if (outcome.indicationId) {
      reportParseFailuresByLta.set(
        outcome.indicationId,
        (reportParseFailuresByLta.get(outcome.indicationId) ?? 0) + 1
      )
    } else {
      unattributableReportFailures += 1
    }
  }
  return {
    reportsByLta,
    reportParseFailuresByLta,
    unattributableReportFailures,
  }
}
